import {
  ENGINE_SFX_STEM_ID,
  ENGINE_SFX_STEM_SOURCE,
  isEngineSfxStemClip,
  isEngineSfxStemLane,
  isSceneSfxClip,
  isSceneSfxLane,
  placeSceneSfxOnOwnLane,
} from './storySoundTracks';

export { ENGINE_SFX_STEM_ID, ENGINE_SFX_STEM_SOURCE, isEngineSfxStemClip };

export function objectStemUrl(url) {
  const text = String(url || '').trim();
  if (!text) return '';
  if (text.startsWith('/api/') || text.startsWith('http://') || text.startsWith('https://')) return text;
  return '';
}

function sceneSpeed(scene) {
  const n = Number(scene?.video_playback_speed ?? scene?.videoPlaybackSpeed ?? 1);
  const speed = Number.isFinite(n) && n > 0 ? n : 1;
  return Math.min(4, Math.max(0.25, speed));
}

function ms(seconds) {
  return Math.round(Number(seconds || 0) * 1000);
}

function sceneTrimWindowMs(scene) {
  const trimStart = Number(scene?.trim_start_seconds || 0);
  const duration = Number(scene?.duration_seconds || 0);
  const rawEnd = scene?.trim_end_seconds;
  let trimEnd = rawEnd == null ? (duration > 0 ? duration : trimStart) : Number(rawEnd);
  if (trimEnd < trimStart) trimEnd = trimStart;
  return { trimStartMs: ms(trimStart), trimEndMs: ms(trimEnd) };
}

export function mixLayoutRows(scenes) {
  let cursor = 0;
  return (scenes || []).map((scene) => {
    const { trimStartMs, trimEndMs } = sceneTrimWindowMs(scene);
    const speed = sceneSpeed(scene);
    const playbackMs = speed ? Math.round((trimEndMs - trimStartMs) / speed) : 0;
    const include = scene?.include_in_video !== false;
    const row = {
      id: String(scene?.id || ''),
      include,
      playback_ms: playbackMs,
      speed: Number(speed.toFixed(4)),
      start_ms: cursor,
      trim_end_ms: trimEndMs,
      trim_start_ms: trimStartMs,
    };
    if (include) cursor += Math.max(0, playbackMs);
    return row;
  });
}

function stableLayout(rows) {
  return JSON.stringify((rows || []).map((row) => ({
    id: String(row.id || ''),
    include: Boolean(row.include),
    playback_ms: Number(row.playback_ms) || 0,
    speed: Number(Number(row.speed || 1).toFixed(4)),
    start_ms: Number(row.start_ms) || 0,
    trim_end_ms: Number(row.trim_end_ms) || 0,
    trim_start_ms: Number(row.trim_start_ms) || 0,
  })));
}

export function isEngineMixStale({ scenes, videoClips, plan } = {}) {
  const current = mixLayoutRows(scenes);
  const mixed = plan?.mix_layout;
  if (!Array.isArray(mixed) || !mixed.length) {
    if (!current.length) return false;
    return Boolean(objectStemUrl(plan?.sfx_stem_url));
  }
  if (stableLayout(current) !== stableLayout(mixed)) return true;
  const slots = new Map();
  (videoClips || []).forEach((clip) => {
    const sid = clip?.sceneId != null ? String(clip.sceneId) : '';
    if (!sid) return;
    const start = Math.round((Number(clip.startTime) || 0) * 1000);
    const prev = slots.get(sid);
    slots.set(sid, prev == null ? start : Math.min(prev, start));
  });
  return current.some((row) => {
    if (!row.include) return false;
    const start = slots.get(row.id);
    if (start == null) return false;
    return Math.abs(start - row.start_ms) > 40;
  });
}

export function engineEventSfxClips(events) {
  return (events || []).flatMap((event) => {
    const url = objectStemUrl(event?.asset?.url);
    if (!url || event?.enabled === false || event?.status === 'skipped') return [];
    const start = Number(event.start_ms);
    if (!Number.isFinite(start)) return [];
    const end = Number(event.end_ms);
    const duration = Math.max(0.1, ((Number.isFinite(end) ? end : start) - start) / 1000);
    const fileDuration = (Number(event.asset?.duration_ms) || 0) / 1000;
    return [{
      id: `engine-event-${event.event_id}`,
      name: event.description || event.category || 'Sound effect',
      url,
      startTime: start / 1000,
      duration,
      sourceDuration: Math.max(duration, fileDuration || duration),
      trimStart: 0,
      volume: 0.85,
      muted: false,
      sourceType: 'scene_sfx',
    }];
  });
}

export function engineSfxStemClip({ url, durationMs, mixSignature } = {}) {
  const duration = Math.max(0.01, (Number(durationMs) || 0) / 1000);
  return {
    id: ENGINE_SFX_STEM_ID,
    name: 'Story SFX',
    url: objectStemUrl(url),
    startTime: 0,
    duration,
    sourceDuration: duration,
    trimStart: 0,
    volume: 1,
    muted: false,
    audioPlaybackSpeed: 1,
    sourceType: ENGINE_SFX_STEM_SOURCE,
    mixSignature: mixSignature || '',
  };
}

export function engineSfxStemMediaTime(timelineTime, clip, { paused = false } = {}) {
  const start = Number(clip?.startTime) || 0;
  const duration = Number(clip?.duration) || 0;
  const rate = Number(clip?.audioPlaybackSpeed) || 1;
  const local = Math.max(0, Number(timelineTime) - start);
  const playing = !paused && timelineTime >= start && timelineTime < start + duration;
  return {
    mediaTime: local * rate,
    playing,
    paused: Boolean(paused) || !playing,
  };
}

export function engineSfxPlaybackState({
  engineEnabled,
  plan,
  scenes,
  videoClips,
  remixUi,
} = {}) {
  if (!engineEnabled) return { mode: 'legacy' };
  const url = objectStemUrl(plan?.sfx_stem_url);
  if (!url) return { mode: 'legacy' };
  if (remixUi === 'failed') {
    return { mode: 'failed', mixSignature: plan?.mix_signature || '' };
  }
  if (plan?.status === 'failed') {
    return { mode: 'updating', mixSignature: plan?.mix_signature || '' };
  }
  if (remixUi === 'updating' || isEngineMixStale({ scenes, videoClips, plan })) {
    return { mode: 'updating', mixSignature: plan?.mix_signature || '' };
  }
  return {
    mode: 'stem',
    mixSignature: plan?.mix_signature || '',
    clip: engineSfxStemClip({
      url,
      durationMs: plan?.sfx_stem_duration_ms,
      mixSignature: plan?.mix_signature,
    }),
  };
}

export function applyEngineSfxPlayback(musicTracks, state) {
  if (!state || state.mode === 'legacy') return musicTracks;
  const next = (musicTracks || []).map((track) => (track || [])
    .filter((clip) => !isEngineSfxStemClip(clip))
    .map((clip) => (isSceneSfxClip(clip) ? { ...clip, muted: true } : clip)));
  if (state.mode === 'stem' && state.clip?.url) {
    return [...next, [state.clip]];
  }
  return next;
}

export function shouldTriggerEngineRemix(state) {
  return state?.mode === 'updating';
}

/**
 * Keep generated sound effects on a timeline lane whenever a stem or timed event exists.
 * Playback can still withhold a stale stem; this only decides what the lane shows.
 */
export function ensureVisibleEngineSfxLane(tracks, plan) {
  const lanes = Array.isArray(tracks) && tracks.length ? tracks : [[]];
  const url = objectStemUrl(plan?.sfx_stem_url);
  const eventClips = engineEventSfxClips(plan?.events);
  if (!url && !eventClips.length) return tracks && tracks.length ? tracks : lanes;
  const flat = lanes.flat();
  const stemOk = !url || flat.some((clip) => isEngineSfxStemClip(clip) && clip?.url === url);
  const eventsOk = eventClips.every((clip) => flat.some((row) => row?.id === clip.id && row?.url === clip.url));
  if (stemOk && eventsOk) return tracks;
  let next = lanes.map((track) => (track || []).filter((clip) => {
    if (url && isEngineSfxStemClip(clip)) return false;
    if (String(clip?.id || '').startsWith('engine-event-')) return false;
    return true;
  }));
  if (!next.length) next = [[]];
  if (eventClips.length) next = placeSceneSfxOnOwnLane(next, eventClips);
  if (url) {
    next = [...next, [engineSfxStemClip({
      url,
      durationMs: plan?.sfx_stem_duration_ms,
      mixSignature: plan?.mix_signature,
    })]];
  }
  return next;
}

/** Sound FX rows first, then music that has clips, then a single empty music drop lane. */
export function orderedMusicLanesForTimeline(tracks) {
  const rows = (Array.isArray(tracks) && tracks.length ? tracks : [[]]).map((track, trackIdx) => ({
    track: Array.isArray(track) ? track : [],
    trackIdx,
  }));
  const sfx = rows.filter((row) => isSceneSfxLane(row.track) || isEngineSfxStemLane(row.track));
  const rest = rows.filter((row) => !sfx.includes(row));
  const filled = rest.filter((row) => row.track.length);
  const empty = rest.filter((row) => !row.track.length);
  const drop = filled.length ? [] : empty.slice(0, 1);
  return [...sfx, ...filled, ...drop];
}
